import React, { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Row,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
} from "reactstrap"
import { setBreadcrumbItems } from "store/actions"
import { connect } from "react-redux"
import apiClient, { API_BASE } from "helpers/apiClient"
import { formatIndianDateTime } from "helpers/indianDate"
import BidRequestModal from "../Home/Properties/BidRequestModal"
import "./BidProperty.scss"

const GET_LISTINGS_FOR_BIDDING_URL = "/api/listings/getListingsForBidding"

interface Listing {
  _id: string
  propertyTitle?: string
  address?: string
  city?: string
  state?: string
  totalSalePrice?: number
  propertyImagesUrls?: string[]
  biddingEnabled?: boolean
  biddingStartPrice?: number
  biddingMinIncrement?: number
  biddingEndDate?: string
  auctionStartDate?: string
  auctionEndDate?: string
  auctionStatus?: "upcoming" | "live" | "ended"
  isUpcoming?: boolean
  reservePrice?: number
  listingStatus?: string
  pricePerSqFt?: number
  roiPercentage?: number
  superAreaSqFt?: number
  annualRent?: number
  currentHighestBid?: number
}

interface BreadcrumbItem {
  title: string
  link: string
}

interface BidListProps {
  setBreadcrumbItems: (title: string, items: BreadcrumbItem[]) => void
}

const formatCurrency = (n: number) => "₹" + (n ?? 0).toLocaleString("en-IN")
const formatDateTime = (d: string) => formatIndianDateTime(d, "–")

const BidList = (props: BidListProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [listings, setListings] = useState<Listing[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [bidModalOpen, setBidModalOpen] = useState(false)
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null)

  useEffect(() => {
    props.setBreadcrumbItems("Bid on Property", [
      { title: "Bidding", link: "#" },
      { title: "Properties for Bidding", link: "#" },
    ])
  }, [props])

  useEffect(() => {
    setLoading(true)
    setError(null)
    apiClient
      .get<{ success: boolean; data?: Listing[]; pagination?: { total: number } }>(GET_LISTINGS_FOR_BIDDING_URL, {
        params: { limit: 50 },
      })
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          setListings(res.data.data)
        } else {
          setListings([])
        }
      })
      .catch(() => {
        setError("Failed to load properties.")
        setListings([])
      })
      .finally(() => setLoading(false))
  }, [])

  // Open bid modal from URL e.g. /bidding?openBid=listingId (redirect from old /bid-request/:id)
  useEffect(() => {
    const openBidId = searchParams.get("openBid")
    if (!openBidId || listings.length === 0) return
    const listing = listings.find((l) => l._id === openBidId)
    if (listing) {
      setSelectedListing(listing)
      setBidModalOpen(true)
      searchParams.delete("openBid")
      setSearchParams(searchParams, { replace: true })
    }
  }, [listings, searchParams, setSearchParams])

  const openBidModal = (item: Listing) => {
    setSelectedListing(item)
    setBidModalOpen(true)
  }

  const closeBidModal = () => {
    setBidModalOpen(false)
    setSelectedListing(null)
  }

  return (
    <Row>
      <Col xl={12}>
        <Card>
          <CardHeader>
            <CardTitle tag="h5" className="mb-0">
              Properties open for bidding
            </CardTitle>
          </CardHeader>
          <CardBody>
            {loading && (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Loading...</p>
              </div>
            )}
            {error && (
              <Alert color="danger">{error}</Alert>
            )}
            {!loading && !error && listings.length === 0 && (
              <Alert color="info">
                No properties are currently open for bidding. Check back later or go to Listings to view all properties.
              </Alert>
            )}
            {!loading && listings.length > 0 && (
              <div className="table-responsive">
                <Table className="table table-bordered align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 60 }}>#</th>
                      <th style={{ width: 120 }}>Action</th>
                      <th>Property</th>
                      <th>Location</th>
                      <th>Start price</th>
                      <th>Min increment</th>
                      <th>Auction start</th>
                      <th>Auction end</th>
                      <th style={{ width: 140 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map((item, idx) => (
                      <tr key={item._id}>
                        <td>{idx + 1}</td>
                        <td>
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => openBidModal(item)}
                          >
                            <i className="mdi mdi-gavel me-1" /> Bid
                          </Button>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {item.propertyImagesUrls?.length ? (
                              <img
                                src={`${API_BASE}${item.propertyImagesUrls[0]}`}
                                alt=""
                                style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }}
                              />
                            ) : (
                              <div
                                className="bg-light rounded"
                                style={{ width: 48, height: 48 }}
                              />
                            )}
                            <strong>{item.propertyTitle || "—"}</strong>
                          </div>
                        </td>
                        <td>{[item.city, item.state].filter(Boolean).join(", ") || "—"}</td>
                        <td>{item.biddingStartPrice != null ? formatCurrency(item.biddingStartPrice) : "—"}</td>
                        <td>{item.biddingMinIncrement != null ? formatCurrency(item.biddingMinIncrement) : "—"}</td>
                        <td>{formatDateTime(item.auctionStartDate || "")}</td>
                        <td>{formatDateTime(item.auctionEndDate || item.biddingEndDate || "")}</td>
                        <td>
                          {item.isUpcoming && (
                            <Badge color="info" className="me-1">Upcoming</Badge>
                          )}
                          {item.auctionStatus === "live" && (
                            <Badge color="success">Live</Badge>
                          )}
                          {item.auctionStatus === "upcoming" && !item.isUpcoming && (
                            <Badge color="secondary">Starts soon</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Col>

      <BidRequestModal
        isOpen={bidModalOpen}
        toggle={closeBidModal}
        listingId={selectedListing?._id}
        startingBid={selectedListing != null ? String(selectedListing.biddingStartPrice ?? 0) : "0"}
        currentHighestBid={selectedListing?.currentHighestBid}
        minBidIncrease={selectedListing?.biddingMinIncrement}
        listingStatus={selectedListing?.listingStatus}
        reservePrice={selectedListing?.reservePrice}
        pricePerSqFt={selectedListing?.pricePerSqFt}
        roiPercentage={selectedListing?.roiPercentage}
        superAreaSqFt={selectedListing?.superAreaSqFt}
        annualRent={selectedListing?.annualRent}
        propertyTitle={selectedListing?.propertyTitle}
        propertyAddress={[selectedListing?.address, selectedListing?.city, selectedListing?.state].filter(Boolean).join(", ")}
        auctionDate={selectedListing?.auctionStartDate}
        onSuccess={closeBidModal}
      />
    </Row>
  )
}

const mapDispatchToProps = { setBreadcrumbItems }
export default connect(null, mapDispatchToProps)(BidList)
